import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

/** Autosizing inline editor — click-to-edit, styled for editorial content. */
export function InlineEditor({
  value, onChange, placeholder, multiline, italic, size = 'md',
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  multiline?: boolean
  italic?: boolean
  size?: 'sm' | 'md' | 'lg'
}) {
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { if (!editing) setDraft(value) }, [value, editing])
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.style.height = 'auto'
      ref.current.style.height = `${ref.current.scrollHeight}px`
    }
  }, [editing, draft])

  const textSize = size === 'lg'
    ? 'text-[15px] leading-relaxed'
    : size === 'sm'
      ? 'text-[12.5px]'
      : 'text-[13.5px] leading-relaxed'
  const baseCls = cn(
    'block w-full rounded-[var(--radius-sm)] px-2 -mx-2 py-1 transition-colors',
    textSize,
    italic && 'italic',
  )

  const commit = () => {
    setEditing(false)
    if (draft !== value) onChange(draft)
  }

  if (!editing) {
    return (
      <div
        onClick={() => { setDraft(value); setEditing(true) }}
        className={cn(
          baseCls,
          'cursor-text',
          value
            ? 'text-text hover:bg-warm-100/60'
            : 'text-text-muted hover:bg-warm-100/60',
        )}
      >
        {value || placeholder}
      </div>
    )
  }

  if (multiline) {
    return (
      <textarea
        ref={ref}
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        rows={1}
        className={cn(
          baseCls,
          'bg-white outline-none resize-none border border-solution/60 focus:border-solution',
        )}
      />
    )
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit() }}
      placeholder={placeholder}
      className={cn(
        baseCls,
        'bg-white outline-none border border-solution/60 focus:border-solution',
      )}
    />
  )
}
